/**
 * VireoHR - Owner Home Screen
 * Ultra-Minimal Design: White, Teal, Gold, Thin Borders
 */

import React, { useState, useEffect } from 'react';

import { Badge } from '@/components/Badge';
import { Card } from '@/components/Card';
import Layout from '@/components/Layout';

interface Employee {
  id: string;
  name: string;
  branch: string;
  clockInTime: string;
  status: 'ON_TIME' | 'LATE';
}

interface Alert {
  id: string;
  type: 'late' | 'no_show' | 'leave_request';
  message: string;
  employeeName: string;
}

export default function OwnerHome() {
  const [currentlyWorking, setCurrentlyWorking] = useState<Employee[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [overtimeEnabled, setOvertimeEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mock data
    setCurrentlyWorking([
      { id: '1', name: 'Ahmed Ali', branch: 'Main Branch', clockInTime: '08:00', status: 'ON_TIME' },
      { id: '2', name: 'Sara Mohammed', branch: 'Downtown Store', clockInTime: '08:15', status: 'LATE' },
      { id: '3', name: 'Omar Hassan', branch: 'Main Branch', clockInTime: '08:05', status: 'ON_TIME' },
    ]);
    
    setAlerts([
      { id: '1', type: 'late', message: 'Clocked in 15 minutes late', employeeName: 'Sara Mohammed' },
      { id: '2', type: 'leave_request', message: 'Requested leave for tomorrow', employeeName: 'Khaled Ibrahim' },
    ]);
    
    setLoading(false);
  }, []);

  // Group employees by branch
  const groupedEmployees = currentlyWorking.reduce((acc, emp) => {
    if (!acc[emp.branch]) acc[emp.branch] = [];
    acc[emp.branch].push(emp);
    return acc;
  }, {} as Record<string, Employee[]>);

  if (loading) {
    return (
      <Layout title="Home">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Home">
      <div className="p-4 space-y-4">
        {/* Date */}
        <div className="text-sm text-gray-600">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>

        {/* Overtime Toggle */}
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Daily Overtime</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {overtimeEnabled ? 'Employees can work past shift end' : 'Auto clock-out at shift end'}
              </p>
            </div>
            <button
              onClick={() => setOvertimeEnabled(!overtimeEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                overtimeEnabled ? 'bg-[#0D9488]' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  overtimeEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </Card>

        {/* Currently Working */}
        <div>
          <h2 className="text-base font-semibold text-gray-900 mb-3">Currently Working ({currentlyWorking.length})</h2>
          <div className="space-y-3">
            {Object.entries(groupedEmployees).map(([branch, employees]) => (
              <Card key={branch}>
                <div className="mb-3">
                  <h3 className="text-sm font-medium text-gray-700">{branch}</h3>
                  <p className="text-xs text-gray-500">{employees.length} employee{employees.length !== 1 ? 's' : ''}</p>
                </div>
                <div className="space-y-2">
                  {employees.map((emp) => (
                    <div key={emp.id} className="flex items-center justify-between py-2 border-t border-gray-100 first:border-t-0">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          emp.status === 'LATE' ? 'bg-amber-500' : 'bg-[#0D9488]'
                        }`} />
                        <span className="text-sm text-gray-900">{emp.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-gray-500">{emp.clockInTime}</span>
                        {emp.status === 'LATE' && (
                          <Badge variant="warning" className="ml-2">Late</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <div>
            <h2 className="text-base font-semibold text-gray-900 mb-3">Alerts</h2>
            <div className="space-y-2">
              {alerts.map((alert) => (
                <Card key={alert.id}>
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      alert.type === 'late' ? 'bg-amber-500' :
                      alert.type === 'no_show' ? 'bg-red-500' :
                      'bg-blue-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{alert.employeeName}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{alert.message}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {currentlyWorking.length === 0 && (
          <Card>
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No employees currently working</p>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}
